<template>
  <DsPageContent v-if="content">
    <DsHero
      :title="content.title"
      :eyebrow="content.eyebrow || content.period"
      with-title-size-large
    />
    <component
      :is="pageSelector"
      :page-content="pageContent"
    />
  </DsPageContent>
</template>
<script setup>
import { DsPageContent, DsHero } from 'awds'
import {
  queryPageInvestorRelationsFinancialsShareholderLetter,
  queryPageInvestorRelationsFinancialsAnnualReport,
  queryFinancialsFinancialRecapCollection
} from '../../data-layer/queries'

const PageContentShareholderLetter = defineAsyncComponent(
  () => import('~/components/page-content/page-content-shareholder-letter.vue')
)
const PageContentAnnualReports = defineAsyncComponent(
  () => import('~/components/page-content/page-content-annual-reports.vue')
)
const PageContentShortTradingUpdate = defineAsyncComponent(
  () => import('~/components/page-content/page-content-short-trading-update.vue')
)
import InvestorDefinitions from '../../../core/data-layer/definitions/investors'

const { getLocaleIso } = useLocales()
const CURRENT_DATE = new Date()
const CURRENT_YEAR = CURRENT_DATE.getFullYear()
const { queryContentful } = useQueries()
const { dataPageMapper } = useDataMapper()
const route = useRoute()
const { locale: localeRef } = useI18n()
const slug = route.params.slug || 'investor-relations'
const locale = getLocaleIso(localeRef.value)
const { useStandardAsyncData, dataLayer } = useInvestorsPageData()
const { content, onBeforeAdyenData, onAfterAdyenData } = useInvestorsPageData()
const store = useRoutesStore()

// We need to deduce the content type
await store.retrieveRoutes()
// We use retrieve routes so then the mapper does not need to fetch them again
const { getRoute } = useRoutes(store.routes, localeRef)
const currentRoute = getRoute(slug)

const { contentType } = currentRoute?.payload || {}
const contentTypeUppercase = 'P' + contentType.slice(1)
const globalDataContentType = !!InvestorDefinitions[contentTypeUppercase]
  ? InvestorDefinitions[contentTypeUppercase].contentType
  : 'PageInvestorRelationsFinancialsShareholderLetter'

await onBeforeAdyenData({
  globalDataContentType: globalDataContentType
})

const { data } = await useAsyncData('investor-relations-financials-detail-' + slug + locale, async () => {
  if (!!InvestorDefinitions[contentTypeUppercase]) {
    const page = await useStandardAsyncData(InvestorDefinitions[contentTypeUppercase], {})

    return {
      contentType,
      pageData: page.pageData
    }
  }

  const queryPage = {
    pageInvestorRelationsFinancialsAnnualReport: queryPageInvestorRelationsFinancialsAnnualReport,
    pageInvestorRelationsFinancialsShareholderLetter: queryPageInvestorRelationsFinancialsShareholderLetter
  }

  const pageSlugData = queryContentful(
    {
      query: queryPage[contentType],
      variables: { slug, locale, preview: useRuntimeConfig().public.preview }
    },
    { log: false }
  )

  const financialsQuery = queryContentful(
    {
      query: queryFinancialsFinancialRecapCollection,
      variables: {
        year: CURRENT_YEAR,
        preview: useRuntimeConfig().public.preview
      }
    },
    { log: false }
  )

  const [data, financialsData] = await Promise.all([pageSlugData, financialsQuery])

  if (!data && financialsData) {
    return {
      pageData: {},
      contentType
    }
  }

  const entry = data.page.items[0]

  const pageData = dataPageMapper(
    {
      ...entry,
      ...{
        financials: {
          year: entry?.highlightsYear || entry?.year,
          fullYearData: financialsData.financialsFullYear,
          halfYearData: financialsData.financialsHalfYear
        }
      }
    },
    dataLayer.mappers
  )

  try {
    await handleLazyUrlsMapping(pageData)
  } catch (e) {
    console.error("[ERROR]: Couldn't handle lazy urls in Financials", e)
  }

  return {
    pageData,
    contentType
  }
})

const componentsMap = new Map([
  ['pageInvestorRelationsFinancialsShareholderLetter', PageContentShareholderLetter],
  ['pageInvestorRelationsFinancialsAnnualReport', PageContentAnnualReports],
  ['pageInvestorRelationsShortTradingUpdate', PageContentShortTradingUpdate]
])

const pageContentType = computed(() => data.value?.contentType || '')
const pageContent = computed(() => ({
  financialData: content.value.financials,
  title: content.value.title,
  period: content.value.eyebrow,
  ...content.value
}))

const pageSelector = computed(() => {
  if (!pageContentType.value) {
    return ''
  }

  return componentsMap.get(pageContentType.value)
})

await onAfterAdyenData(data.value)
</script>
